// Reconciles server-side user preferences with the local chat store.
// Pattern:
//   1. On app boot, fetch preferences. If server has a default
//      provider/model AND it's still in the model catalog, apply it.
//   2. When the user picks a model, cache to localStorage (via
//      chatStore's persist middleware) AND write back to the server.
//   3. When Settings panel saves different defaults, push the update
//      into chatStore so the ModelPicker reflects it immediately.
//
// This is a classic "cache + source of truth" pattern: localStorage
// gives instant renders after reload, server-side prefs survive cross-
// device, and the catalog validates that the cached value is still
// real (not a deprecated model id).

import { toast } from 'sonner';
import { getPreferences, updatePreferences, type Preferences } from './preferences';
import { listModels } from './models';
import { useChatStore } from '@/stores/chatStore';
import type { Provider } from '@/types/api';

/** Pull preferences from server and push into chatStore if newer. */
export async function hydratePreferencesIntoChatStore(): Promise<void> {
  try {
    const [prefs, catalog] = await Promise.all([getPreferences(), listModels()]);
    applyValidatedSelection(prefs, catalog);
  } catch (err) {
    // Non-fatal: localStorage cache still renders; user can pick via
    // ModelPicker. Log quietly — we don't want a toast on every boot
    // if the token is briefly invalid.
    console.warn('[prefs] hydrate skipped:', err);
  }
}

/** Apply the server's default selection, falling back to a safe catalog
 *  entry if the saved model id isn't recognized anymore. */
function applyValidatedSelection(
  prefs: Preferences,
  catalog: { providers: Provider[]; models: Record<Provider, Array<{ id: string }>> },
) {
  const sProvider = prefs.default_provider;
  const sModel = prefs.default_model;
  if (!sProvider || !sModel) return; // nothing on the server yet

  const providerList = catalog.providers ?? [];
  if (!providerList.includes(sProvider)) {
    console.warn('[prefs] saved provider no longer in catalog:', sProvider);
    return;
  }
  const models = catalog.models?.[sProvider] ?? [];
  if (!models.some((m) => m.id === sModel)) {
    console.warn('[prefs] saved model no longer in catalog:', sProvider, sModel);
    return;
  }
  useChatStore.getState().setProviderModel(sProvider, sModel);
}

/** Fire-and-forget write-back when the user picks a new model. Dedupes
 *  rapid selections via a simple microtask guard. */
let saveDebounce: ReturnType<typeof setTimeout> | null = null;
export function saveSelectionToServer(provider: Provider, model: string) {
  if (saveDebounce) clearTimeout(saveDebounce);
  saveDebounce = setTimeout(async () => {
    try {
      await updatePreferences({ default_provider: provider, default_model: model });
    } catch (err) {
      // Keep the local change either way — next hydrate will retry. But
      // surface the failure so the user isn't left thinking it saved.
      toast.error('Could not save default model to server', {
        description: (err as Error).message,
      });
    }
  }, 400);
}
